import type { AgentConfig } from '../../../../services/authService';
import type { PosAgentFormValues, PosAgentPayload, PosSetupSavePayload } from '../../../../types/features/storeDetails/posSetup';

export const POS_SETUP_EXTENSIONS = ['pdf', 'xml', 'log', 'txt', 'csv'];
export const VERIFONE_DEFAULT_EXTENSIONS = ['xml', 'txt', 'pdf', 'log'];

export const getAgentDefaults = (config?: AgentConfig | null): PosAgentFormValues => ({
    scannerProxyEnabled: config?.config?.ScannerProxy?.Enabled ?? true,
    scannerProxyPort: config?.config?.ScannerProxy?.Port ?? '',
    readPath: config?.config?.posConfig?.ReadPath ?? '',
    readEnabled: config?.config?.PosDataUpload?.Enabled ?? true,
    writePath: config?.config?.posConfig?.WritePath ?? '',
    writeEnabled: config?.config?.PosDataDownload?.Enabled ?? true,
    eligibleExtensions: config?.config?.posConfig?.EligibleExtensions ?? [],
    includeSubdirectories: config?.config?.posConfig?.IncludeSubdirectories ?? true,
    posIpAddress: config?.config?.NetworkShare?.Root ?? '',
    posUsername: config?.config?.NetworkShare?.User ?? '',
    posPassword: config?.config?.NetworkShare?.Password ?? '',
});

const normalizePosType = (value?: string | null): string => (value || '').trim().toLowerCase();

export const isGilbarcoType = (value: string): boolean => {
    const normalized = normalizePosType(value);
    return normalized.includes('gilbarco') || normalized.includes('passport');
};

export const isVerifoneType = (value: string): boolean => {
    const normalized = normalizePosType(value);
    return normalized.includes('verifone') || normalized.includes('verifon');
};

export const isExatouchType = (value: string): boolean => normalizePosType(value).includes('exatouch');

export const isNrsType = (value: string): boolean => normalizePosType(value) === 'nrs';

export const buildPosSetupPayload = (
    posType: string,
    agentForm: PosAgentFormValues,
    showAgentFields: boolean,
    showVerifoneFields: boolean,
): PosSetupSavePayload => {
    if (!showAgentFields) {
        return { posType };
    }

    const agentPayload: PosAgentPayload = {
        PosDataUpload: {
            Enabled: Boolean(agentForm.readPath.trim()) && agentForm.readEnabled,
        },
        PosDataDownload: {
            Enabled: Boolean(agentForm.writePath.trim()) && agentForm.writeEnabled,
        },
        ScannerProxy: {
            Enabled: agentForm.scannerProxyEnabled,
            Port: agentForm.scannerProxyPort.trim(),
        },
        posConfig: {
            ReadPath: agentForm.readPath.trim(),
            WritePath: agentForm.writePath.trim(),
            IncludeSubdirectories: agentForm.includeSubdirectories,
            EligibleExtensions: agentForm.eligibleExtensions,
        },
        ...(showVerifoneFields
            ? {
                  NetworkShare: {
                      Root: agentForm.posIpAddress.trim() || null,
                      User: agentForm.posUsername.trim() || null,
                      Password: agentForm.posPassword.trim() || null,
                  },
              }
            : {}),
    };

    return {
        posType,
        agentPayload,
    };
};
